import {
  Button,
  ButtonGroup,
  Card,
  Choice,
  closeCurrentDialog,
  DataStatus,
  showNotification,
} from 'platform/components';
import {Hide, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useDeactivateBranchWithReassignmentMutation,
  useGetAssignedUsersCountQuery,
  useGetBranchListQuery,
  useGetBranchQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

type DeactivateBranchDialogContentProps = {
  branchId: string;
  onSuccess?: () => void;
};

export function DeactivateBranchDialogContent(props: DeactivateBranchDialogContentProps) {
  const [replacementBranchId, setReplacementBranchId] = useState<string | null>(null);

  const {
    data: assignedUsers,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useGetAssignedUsersCountQuery({branchId: props.branchId});

  const {data: branchInfo, isLoading: isBranchLoading} = useGetBranchQuery({
    branchId: props.branchId,
  });

  const {
    data: branchList,
    isLoading: isBranchListLoading,
    isError: isBranchListError,
  } = useGetBranchListQuery();

  const [deactivateBranchWithReassignment, {isLoading: isDeactivating}] =
    useDeactivateBranchWithReassignmentMutation();

  const isCountKnown = isNotNil(assignedUsers);
  const count = assignedUsers?.count ?? 0;
  const hasAssignedUsers = count > 0;
  const isDataStatusLoading = isCountLoading || isBranchLoading || isBranchListLoading;
  const isDataStatusError = isCountError || isBranchListError;

  const replacementOptions = (branchList?.branchListItems ?? [])
    .filter((branch) => branch.id !== props.branchId)
    .map((branch) => ({value: branch.id, label: branch.marketingName ?? ''}));

  const handleConfirm = () => {
    deactivateBranchWithReassignment({
      branchId: props.branchId,
      replacementBranchId: hasAssignedUsers ? replacementBranchId : null,
    })
      .unwrap()
      .then(() => {
        const message = hasAssignedUsers
          ? i18n.t('page.settings.labels.deactivateBranchSuccessWithUsers', {
              count,
              branch:
                replacementOptions.find((option) => option.value === replacementBranchId)?.label ??
                '',
            })
          : i18n.t('page.settings.labels.deactivateBranchSuccessNoUsers');

        showNotification.success(message);
        closeCurrentDialog();
        props.onSuccess?.();
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <DataStatus isLoading={isDataStatusLoading} isError={isDataStatusError} minHeight={30}>
        <Show when={hasAssignedUsers}>
          <VStack spacing={4}>
            <Card variant="inlineGrey">
              <Text color="secondary" size="small">
                {i18n.t('page.settings.labels.deactivateBranchReassignmentNotice')}
              </Text>
            </Card>
            <Choice
              label={i18n.t('page.settings.labels.deactivateBranchPickerLabel', {count})}
              placeholder={i18n.t('page.settings.labels.deactivateBranchPickerPlaceholder')}
              options={replacementOptions}
              value={replacementBranchId}
              onChange={setReplacementBranchId}
              data-testid="deleteBranchDialogReplacementBranch"
              menuInPortal
            />
          </VStack>
        </Show>

        <Hide when={hasAssignedUsers}>
          <Text>
            {i18n.t('page.settings.labels.deactivateBranchDescription', {name: branchInfo?.name})}
          </Text>
        </Hide>
      </DataStatus>
      <ButtonGroup align="right">
        <Button
          data-testid="deleteBranchDialogCancel"
          variant="secondary"
          onClick={closeCurrentDialog}
          title={i18n.t('general.actions.cancel')}
        />
        <Button
          data-testid="deleteBranchDialogConfirm"
          variant="primary"
          onClick={handleConfirm}
          isLoading={isDeactivating}
          isDisabled={
            isDataStatusLoading ||
            isDataStatusError ||
            !isCountKnown ||
            (hasAssignedUsers && isNil(replacementBranchId))
          }
          title={i18n.t('general.actions.confirm')}
        />
      </ButtonGroup>
    </VStack>
  );
}
